// @ts-check
import React from 'react';

import Button from '@liquidweb-dev/undercurrent/Button';
import Drawer from '@liquidweb-dev/undercurrent/Drawer';
import SupportConversation from 'components/standard/SupportConversation/SupportConversation';
import SupportStatusChip from 'components/standard/SupportStatusChip';
import NotRespondableBanner from 'containers/pages/support/ticket/NotRespondableBanner';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import useAssetDetails from 'modules/queries/asset/useDetails';
import useSupportRequestDetails from 'modules/queries/support/request/useDetails';
import { Link as RouterLink } from 'react-router-dom';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerHeader from '@liquidweb-dev/undercurrent/DrawerHeader';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';

dayjs.extend(localizedFormat);

/**
 * @param {object} props
 * @param {string} props.label
 * @param {React.ReactNode} props.children
 * @param {import('@liquidweb-dev/undercurrent/Stack').StackProps['sx']} [props.sx]
 */
const Detail = ({ label, children, sx }) => {
	return (
		<Stack gap={50} sx={sx}>
			<Typography
				variant="label-lg"
				sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
			>
				{label}
			</Typography>
			{children}
		</Stack>
	);
};

/**
 * @typedef {Object} DrawerCasePreviewProps
 * @property {() => void} onClose
 * @property {string} status
 * @property {string} subject
 * @property {string} createdDate
 * @property {string} ticketNumber
 * @property {string} caseId
 * @property {string} description
 * @property {boolean} respondable
 * @property {string | null} [desiredLwTeam]
 * @property {string | null} [subType]
 * @property {string[]} [ccList]
 * @property {string} path
 * @property {import('@liquidweb-dev/club-sauce/public/support/request/index.raw').LWApiPublicSupportRequestDetailsResultRawI['subaccntHashRef']} [subaccntHashRef]
 * */

/** @param {Omit<import('@liquidweb-dev/undercurrent/Drawer').DrawerProps, 'onClose'> & DrawerCasePreviewProps} drawerProps */
const DrawerCasePreview = ({
	onClose,
	status,
	subject,
	createdDate,
	ticketNumber,
	caseId,
	description,
	respondable,
	subaccntHashRef,
	desiredLwTeam,
	subType,
	ccList,
	path,
	...drawerProps
}) => {
	const { data: assetData } = useAssetDetails(
		{ uniq_id: subaccntHashRef?.uniq_id || '', alsowith: ['product'] },
		{ enabled: Boolean(subaccntHashRef?.uniq_id) },
	);

	const productData = assetData?.product;
	const productTitle = productData?.title || productData?.sub_title;

	const { data: caseDetails, isLoading } = useSupportRequestDetails(
		{
			ticket_number: ticketNumber,
			alsowith: ['comments', 'customComments', 'emailsWithAttachmentDetails'],
		},
		{ enabled: Boolean(ticketNumber) },
	);

	return (
		<Drawer anchor="right" onClose={onClose} {...drawerProps}>
			<DrawerHeader
				onClose={onClose}
				description={`Case number ${ticketNumber}`}
			>
				{subject}
			</DrawerHeader>

			<DrawerContent sx={{ gap: 300 }}>
				<Stack direction="row" gap={300}>
					<Detail label="Status">
						<SupportStatusChip status={status} />
					</Detail>

					{productTitle && (
						<Detail label="Product" sx={{ minWidth: 0 }}>
							<TypographyOverflow variant="body-sm">
								{productTitle}
							</TypographyOverflow>
						</Detail>
					)}

					<Detail label="Created">
						<Typography variant="body-sm">
							{dayjs(createdDate).format('l')}
						</Typography>
					</Detail>
				</Stack>

				{!respondable && (
					<NotRespondableBanner
						id={caseId}
						subject={subject}
						desired_lw_team={desiredLwTeam}
						sub_type={subType}
						cc_list={ccList}
						subaccntHashRef={subaccntHashRef}
					/>
				)}

				<SupportConversation
					caseId={caseId}
					comments={caseDetails?.comments}
					createdDate={createdDate}
					customComments={caseDetails?.customComments}
					emailsWithAttachmentDetails={caseDetails?.emailsWithAttachmentDetails}
					description={description}
					respondable={respondable}
					onCancelReply={onClose}
					isLoading={isLoading}
				/>

				<Button
					component={RouterLink}
					// @ts-ignore
					to={
						path === 'migration-center'
							? `/support/mycases/${ticketNumber}?fromMigrationList=true`
							: `/support/mycases/${ticketNumber}`
					}
					variant="secondary"
					size="large"
				>
					View case
				</Button>
			</DrawerContent>
		</Drawer>
	);
};

export default DrawerCasePreview;
